import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import ThreeJSFrameViewer, { NavigationProps } from "./ThreeJSFrameViewer";
import TopLayer from "./TimelineNavigation/TopLayer";
import BottomLayer from "./TimelineNavigation/BottomLayer";
import { EasingFunctionType } from "./types";

const ResponsiveContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  @media (max-width: 999px) {
    display: flex;
    flex-direction: column;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  @media (max-width: 999px) {
    height: auto;
    flex: 1;
    margin-bottom: 8px;
  }
`;

const MobileTopLayer = styled.div`
  width: 100%;
  order: -1;

  @media (min-width: 1000px) {
    display: none;
  }
`;

const MobileBottomLayer = styled.div`
  width: 100%;
  order: 1;

  @media (min-width: 1000px) {
    display: none;
  }
`;

interface ResponsiveVideoWrapperProps {
  jsonPath: string;
  framesPath: string;
  transitionDuration?: number;
  easingFunction?: EasingFunctionType;
  adaptiveSpeed?: boolean;
  maxDuration?: number;
  frameSkipRate?: number;
  onVideoChanged?: (index: number, totalVideos: number) => void;
  onTransitionChange?: (isTransitioning: boolean) => void;
  navigatorRef?: React.RefObject<{
    nextVideo: () => void;
    prevVideo: () => void;
    goToVideo: (index: number) => void;
  } | null>;
}

export default function ResponsiveVideoWrapper(props: ResponsiveVideoWrapperProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [navigationProps, setNavigationProps] = useState<NavigationProps | null>(null);

  // Handle window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1000);
    };

    // Initial check
    checkMobile();

    // Add resize listener
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Handle external navigation rendering for mobile - wrapped in useCallback to prevent infinite loops
  const handleExternalNavigation = useCallback((navProps: NavigationProps) => {
    setNavigationProps(navProps);
  }, []);

  return (
    <ResponsiveContainer>
      {/* Mobile TopLayer - only shows on mobile */}
      {navigationProps && (
        <MobileTopLayer>
          <TopLayer
            currentVideo={navigationProps.videos[navigationProps.currentVideoIndex]}
            isTransitioning={navigationProps.isTransitioning}
          />
        </MobileTopLayer>
      )}

      {/* Video Container */}
      <VideoContainer>
        <ThreeJSFrameViewer
          {...props}
          hideTimelineNavigation={isMobile}
          renderExternalNavigation={isMobile ? handleExternalNavigation : undefined}
        />
      </VideoContainer>

      {/* Mobile BottomLayer - only shows on mobile */}
      {navigationProps && (
        <MobileBottomLayer>
          <BottomLayer {...navigationProps} />
        </MobileBottomLayer>
      )}
    </ResponsiveContainer>
  );
}
